// services/departureApi.js
//
// Frontend API surface for the departure-readiness endpoints
// (tasks/checkout-message-automation.md §3.9). Uses the shared secureApi instance — the auth header
// is attached automatically. Each call returns the parsed response body.
//
// Its OWN service file rather than a section of lockApi: the arrival equivalents live there only
// because that surface began as a lock concern. A departure has no lock involvement at all, and
// when the in-unit keypad adds a second signal it belongs here, not under locks.

import { secureApi } from './secureApi';

const departureApi = {
  /**
   * Today's departures, each with its checkout-reminder status.
   *
   * ONE day, not two (unlike arrivals): the reminder fires 60 minutes before departure, so the
   * guests whose reminder needs attention today ARE today's departures.
   *
   * Recomputed server-side per request from the same eligibility rulebook the sender uses, so the
   * card can never disagree with what actually went out.
   */
  getTodayDepartureReadiness: async () => {
    const response = await secureApi.get('/api/v1/departures/readiness');
    return response.data;
  },

  /**
   * "I sent this guest their departure reminder myself."
   *
   * Gathern / manual / direct bookings have no OTA thread, so the automation can never deliver
   * their reminder — the card shows them as an action until a human acknowledges it. Trust-based,
   * audit-trailed server-side.
   *
   * `preserveBackendError` so the caller can read the specific refusal (an OTA booking, which must
   * NOT be tickable) instead of secureApi's generic text.
   */
  markCheckoutMessageSentManually: async (bookingId) => {
    const response = await secureApi.post(
      `/api/v1/departures/${bookingId}/manual-send`,
      {},
      { preserveBackendError: true }
    );
    return response.data;
  },

  /**
   * Undo a mis-tick. The acknowledgement is a human assertion with nothing to verify against, so it
   * will sometimes land on the wrong row. Both the tick and the undo stay on the booking's change log.
   */
  undoCheckoutMessageSentManually: async (bookingId) => {
    const response = await secureApi.delete(
      `/api/v1/departures/${bookingId}/manual-send`,
      { preserveBackendError: true }
    );
    return response.data;
  },

  /**
   * "Operations confirms this guest had left, at this time."
   * (tasks/manual-guest-checkout-event.md)
   *
   * A different assertion from the manual-send pair above — those are about a MESSAGE, this is
   * about whether the guest actually left. RECORD ONLY: it notifies nobody and dispatches nothing.
   *
   * @param {string} bookingId
   * @param {string} observedAt - ISO instant of when the guest LEFT, not when the button was
   *   pressed. Composed against Asia/Riyadh by the caller (`riyadhInputValueToIso`), because a
   *   datetime-local field carries no timezone and the backend bounds-checks a real instant.
   */
  recordGuestCheckout: async (bookingId, observedAt) => {
    const response = await secureApi.post(
      `/api/v1/departures/${bookingId}/checked-out`,
      { observedAt },
      { preserveBackendError: true }
    );
    return response.data;
  },

  /**
   * Withdraw a checkout record. It will land on the wrong row sometimes — it is a human assertion
   * made from a table of similar-looking guests.
   *
   * The record is MARKED withdrawn, never deleted: ops must be able to see that a claim was made
   * and retracted, not an absence. Both directions also stay on the booking's change log.
   *
   * ⚠️ `source` IS REQUIRED IN PRACTICE, even though it defaults server-side. A booking can now
   * carry an ops record AND a guest's own plaque report, and the endpoint withdraws the one it is
   * told about. Omit it against a guest-sourced record and the request matches nothing, returns a
   * cheerful success, and the row comes back unchanged — a button that reports success and does
   * nothing, which nobody ever investigates. Pass the source of the record being shown.
   *
   * @param {string} bookingId
   * @param {'manual'|'guest'} source  the source of the record on screen
   */
  undoGuestCheckout: async (bookingId, source = 'manual') => {
    const response = await secureApi.delete(
      `/api/v1/departures/${bookingId}/checked-out?source=${encodeURIComponent(source)}`,
      { preserveBackendError: true }
    );
    return response.data;
  }
};

export default departureApi;
